import React, { useCallback, useEffect, useRef, useState } from 'react';
import './TableWrap.css';

/**
 * TableWrap (DataTable-обёртка) — оформление для обычной <table> страницы,
 * без переписывания её данных и разметки строк (аудит U5).
 *
 *   <TableWrap density="compact" maxHeight="70vh" stickyFirstColumn>
 *     <table>…как было…</table>
 *   </TableWrap>
 *
 *   density:          'normal' (строка ~44 px) | 'compact' (~36 px)
 *   stickyHeader:     шапка липнет при вертикальной прокрутке (по умолчанию да).
 *                     Липнет внутри обёртки — задайте maxHeight, иначе обёртка
 *                     растёт по содержимому и прокручивается вся страница.
 *   maxHeight:        '70vh' / 480 — высота области с прокруткой
 *   stickyFirstColumn: первая колонка стоит при горизонтальной прокрутке
 *   striped:          зебра (по умолчанию нет — только подсветка строки)
 *   bordered:         рамка и скругление вокруг (по умолчанию да)
 *
 * Широкая таблица прокручивается по горизонтали, а у края, за которым ещё есть
 * колонки, появляется тень-подсказка. Классы в ячейках: .num — число вправо
 * табличными цифрами, .code — моноширинный код, .nowrap — без переноса.
 * Стили обёртки нулевой специфичности (:where) — классы самой страницы сильнее.
 */
export function TableWrap({
  children,
  density = 'normal',
  stickyHeader = true,
  stickyFirstColumn = false,
  maxHeight,
  striped = false,
  bordered = true,
  className = '',
  style,
  ...rest
}) {
  const scrollRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges(prev => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      if (ro) ro.disconnect();
    };
  }, [update]);

  // Данные могли смениться (подгрузились строки) — пересчитать края
  useEffect(() => { update(); });

  const cls = [
    'ui-tw', `ui-tw--${density}`,
    stickyHeader && 'ui-tw--sticky',
    stickyFirstColumn && 'ui-tw--sticky-col',
    striped && 'ui-tw--striped',
    bordered && 'ui-tw--bordered',
    edges.left && 'ui-tw--shadow-left',
    edges.right && 'ui-tw--shadow-right',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={cls} style={style} {...rest}>
      <div
        ref={scrollRef}
        className="ui-tw__scroll"
        style={maxHeight ? { maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight } : undefined}
      >
        {children}
      </div>
    </div>
  );
}

export default TableWrap;
